import {
  IParentTicketItem,
  ISimpleEventDetail,
  ProductType,
  SupportedLanguage,
} from "interfaces";
import { useOne, useTranslation } from "@refinedev/core";
import { SELLING_TYPE } from "utills/constants";
import { get } from "lodash";
import { Label } from "components/label";
import { SwitchButton } from "components/switchButton";
import { RightSideModal } from "components/rightSideModal";
import { IModalProps } from "components/modal";
import { endpointService } from "components/shared/services/endpointService";
import Input from "components/input";

interface ParentTicketProps extends IModalProps {
  eventDetailData: ISimpleEventDetail;
  ticketData?: IParentTicketItem | null;
}

export const ParentTicket = ({
  title,
  open,
  onClose,
  eventDetailData,
  ticketData,
}: ParentTicketProps) => {
  const { translate } = useTranslation();
  const { data } = useOne({
    resource: endpointService.getEventEndpoint(
      "getAttendantTemplateByEventId",
      {
        eventId: eventDetailData.id ?? "",
      }
    ),
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!eventDetailData.id,
    } as any,
  });

  const templatesDataArr = (data?.data ?? []).map((template: any) => ({
    label: get(template, "attendant_template_name", ""),
    value: get(template, "id", ""),
  }));

  return (
    <RightSideModal title={title ?? ""} open={open} onClose={onClose}>
      <div className="grid sm:grid-cols-6 gap-y-6 gap-x-4 p-4 text-sm sm:px-6">
        <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
          <p className="text-sm">
            {translate("eventSetup.phaseAndTicket.modal.ticket.ticketInfo")}
          </p>
        </div>
        <div className="col-span-full">
          <Label
            required
            id={`category_${eventDetailData.languages[0] ?? "en"}`}
            name={translate("eventSetup.phaseAndTicket.modal.ticket.category")}
          />
          <div className="flex flex-col gap-y-3">
            {eventDetailData.languages.map((lang: SupportedLanguage) => (
              <div key={lang}>
                <div className="relative flex items-center gap-x-2">
                  <img
                    src={`/images/flags/circle-${lang}.png`}
                    alt="Language Icon"
                    className="size-5"
                  />
                  {get(ticketData?.category, lang, "")}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="sm:col-span-3">
          <Label
            id="selling_min_qty"
            name={translate(
              "eventSetup.phaseAndTicket.modal.ticket.minSelectionQuantity"
            )}
            required
          />
          {get(ticketData, "selling_min_qty", 0)}
        </div>
        <div className="sm:col-span-3">
          <Label
            id="selling_max_qty"
            name={translate(
              "eventSetup.phaseAndTicket.modal.ticket.maxSelectionQuantity"
            )}
            required
          />
          {get(ticketData, "selling_max_qty", 0)}
        </div>
        <div className="sm:col-span-3">
          <Label
            id="selling_type"
            name={translate(
              "eventSetup.phaseAndTicket.modal.ticket.sellingType"
            )}
            required
          />
          {
            SELLING_TYPE.find(
              (selected: { label: string; value: string }) =>
                selected.value === get(ticketData, "selling_type", "")
            )?.label
          }
        </div>
        <div className="sm:col-span-3">
          {ticketData?.product_type === ProductType.TICKET ? ( // hidden for group tickets
            <>
              <div className="sm:col-span-3">
                <Label
                  id="is_use_private_flow"
                  name={translate(
                    "eventSetup.phaseAndTicket.modal.ticket.useForPrivateFlow"
                  )}
                />
                <Input
                  type="checkbox"
                  label=""
                  id="is_use_private_flow"
                  checked={ticketData?.is_use_private_flow ? true : false}
                  onChange={() => {}}
                  disabled
                />
              </div>
            </>
          ) : (
            <></>
          )}
        </div>
        <div className="col-span-full pb-2 border-b-[1px] border-b-gray-200">
          <p className="text-sm">
            {translate(
              "eventSetup.phaseAndTicket.modal.ticket.participantFormInfo"
            )}
          </p>
        </div>
        <div className="col-span-full w-fit">
          <SwitchButton
            label={translate(
              "eventSetup.phaseAndTicket.modal.ticket.isPackage"
            )}
            checked={ticketData?.is_package ?? false}
            onChange={() => {}}
            disabled
          />
        </div>
        <div className="col-span-full">
          <div className="divide-y divide-gray-300">
            {(ticketData?.attendant_template ?? []).map(
              (field, index: number) => (
                <div key={index} className="py-4">
                  <div>
                    <Label
                      id={`attendant_template.${index}.template_id`}
                      name={translate(
                        "eventSetup.phaseAndTicket.modal.ticket.selectParticipantForm"
                      )}
                      required
                    />
                    {
                      templatesDataArr.find(
                        (selected: { label: string; value: string }) =>
                          selected.value === get(field, "template_id", "")
                      )?.label
                    }
                  </div>
                  {ticketData?.is_package ? (
                    <div className="mt-4">
                      <Label
                        id={`attendant_template.${index}.name`}
                        name={translate(
                          "eventSetup.phaseAndTicket.modal.ticket.formNameDisplayed"
                        )}
                      />
                      <div className="flex flex-col gap-y-3">
                        {eventDetailData.languages.map((lang) => (
                          <div key={lang}>
                            <div className="relative flex items-center gap-x-2">
                              <img
                                src={`/images/flags/circle-${lang}.png`}
                                alt="Language Icon"
                                className="size-5"
                              />
                              {get(field.name, lang, "")}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <></>
                  )}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </RightSideModal>
  );
};
